import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";

import Icon from "@/components/atoms/Icon";
import { OfficeSelectorForm } from "@/components/forms/OfficeSelectorForm";
import { Office } from "@/graphql/sdk/__generated__";
import { useTranslation } from "@/i18n/client";
import { AllowedLanguages } from "@/types/generic";

interface OfficeSelectorModalProps {
  locale: AllowedLanguages;
  onSelectOffice: (office: Office) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const OfficeSelectorModal = ({
  open,
  setOpen,
  locale,
  onSelectOffice,
}: OfficeSelectorModalProps) => {
  const { t } = useTranslation(locale);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={setOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen">
          <div className="flex min-h-full items-center justify-center p-4 sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full rounded-xl bg-white p-10 shadow-xl sm:max-w-xl">
                <Dialog.Title as="h4">
                  {t("components.office_selector_modal.title")}
                </Dialog.Title>
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="ml-auto text-gray-700"
                    onClick={() => setOpen(false)}
                  >
                    <span className="sr-only">Close</span>
                    <Icon name="close" className="h-5 w-5 text-black-300" />
                  </button>
                </div>

                <OfficeSelectorForm
                  locale={locale}
                  i18n={{
                    error_required: t("common.error.required"),
                    search_location_label: t(
                      "components.office_selector_modal.search_location.label",
                    ),
                    search_location_placeholder: t(
                      "components.office_selector_modal.search_location.placeholder",
                    ),
                    select_office: t(
                      "components.office_selector_modal.select_office",
                    ),
                    use_current_location: t(
                      "components.office_selector_modal.search_location.use_current",
                    ),
                  }}
                  onSelectOffice={onSelectOffice}
                />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
